import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, ArrowUpRight, Instagram } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import { Eyebrow } from "@/components/site/ui";
import { PORTFOLIO, PORTFOLIO_CATEGORIES, CONTACT, EKTIREN, waLink } from "@/data/site";

export default function Referanslar() {
  const [filter, setFilter] = useState("all");
  const items = filter === "all" ? PORTFOLIO : PORTFOLIO.filter((p) => p.cat === filter);

  return (
    <div className="pt-4">
      <section className="relative py-20 sm:py-28 overflow-hidden grain">
        <div className="absolute top-0 right-1/4 w-[420px] h-[420px] bg-neon-magenta/20 blur-[130px] rounded-full" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <Eyebrow>Referanslar &amp; Portföy</Eyebrow>
          <h1 className="mt-6 font-display font-extrabold text-5xl sm:text-7xl leading-[0.95]">
            <MaskedLines lines={["Konuşan işler,", "büyüyen markalar"]} lineClassName="[&:nth-child(2)]:text-gradient" />
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-7 max-w-2xl text-lg text-slate-300">Yönettiğimiz hesaplar ve ürettiğimiz içeriklerden seçkiler. Her proje, ölçülebilir bir büyüme hikayesi.</p>
          </Reveal>
        </div>
      </section>

      {/* Filters + grid */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap gap-2 mb-10">
            {PORTFOLIO_CATEGORIES.map((c) => (
              <button
                key={c.key}
                onClick={() => setFilter(c.key)}
                data-testid={`portfolio-filter-${c.key}`}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                  filter === c.key
                    ? "bg-gradient-to-r from-cobalt to-neon-magenta text-white"
                    : "glass border border-white/10 text-slate-300 hover:text-white"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>

          <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <AnimatePresence mode="popLayout">
              {items.map((p) => (
                <motion.div
                  key={p.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.25 }}
                  data-testid={`portfolio-item-${p.id}`}
                >
                  <a
                    href={CONTACT.instagramUrl} target="_blank" rel="noreferrer"
                    className="group relative block rounded-3xl overflow-hidden border border-white/10 aspect-[4/5]"
                  >
                    <img src={p.img} alt={p.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/40 to-transparent" />
                    <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-cobalt to-neon-magenta text-white">{p.metric}</span>
                    <span className="absolute top-4 right-4 p-2 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                      <ArrowUpRight className="w-4 h-4 text-white" />
                    </span>
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <h3 className="font-display text-xl font-bold">{p.title}</h3>
                      <span className="mt-1 inline-flex items-center gap-1.5 text-sm text-slate-300">
                        <Instagram className="w-3.5 h-3.5 text-neon-pink" /> {p.client}
                      </span>
                    </div>
                  </a>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <Reveal className="neon-border rounded-3xl glass-strong p-10 text-center glow-magenta">
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold">Sıradaki başarı hikayesi seninki olsun</h2>
            <p className="mt-3 text-slate-300">Markanı bir sonraki seviyeye taşımak için hemen görüşelim.</p>
            <a
              href={waLink(CONTACT.phoneRaw, "Merhaba, referanslarınızı inceledim, markam için bilgi almak istiyorum.")}
              target="_blank" rel="noreferrer"
              data-testid="referanslar-cta-whatsapp"
              className="mt-7 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-cobalt to-neon-magenta text-white font-semibold text-lg hover:scale-[1.03] transition-transform"
            >
              <MessageCircle className="w-5 h-5" /> Projeni Konuşalım
            </a>
            <p className="mt-4 text-[11px] font-mono text-slate-500">Dijital Altyapı: {EKTIREN.name}</p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
